<template>
  <div class="landing">
    <section class="landing-hero">
      <v-container class="landing-wrap">
        <v-row align="center">
          <v-col cols="12" md="6" class="pr-md-8">
            <p class="landing-kicker">{{ l.hero.kicker }}</p>
            <h1 class="landing-h1">{{ l.hero.title }}</h1>
            <p class="landing-lead" v-html="l.hero.lead"></p>
            <div class="landing-actions">
              <v-btn x-large depressed color="success" :to="l.hero.primary.to">
                {{ l.hero.primary.text }}
                <v-icon right>mdi-arrow-right</v-icon>
              </v-btn>
              <v-btn x-large outlined color="success darken-2" :to="l.hero.secondary.to">
                {{ l.hero.secondary.text }}
              </v-btn>
            </div>
            <p class="landing-note">
              <v-icon small class="mr-1">mdi-cellphone-link</v-icon>{{ l.hero.note }}
            </p>
          </v-col>
          <v-col cols="12" md="6">
            <div class="landing-hero-visual">
              <app-preview variant="shootingbook" />
              <app-preview variant="phone" class="landing-hero-phone d-none d-sm-block" />
            </div>
          </v-col>
        </v-row>
      </v-container>
    </section>

    <section class="landing-intro">
      <v-container class="landing-narrow">
        <h2 class="landing-h2">{{ l.intro.title }}</h2>
        <p class="landing-text" v-html="l.intro.text"></p>
      </v-container>
    </section>

    <section
      v-for="(feature, index) in l.features"
      :id="feature.id"
      :key="feature.id"
      class="landing-feature"
      :class="{ 'landing-feature--tinted': index % 2 === 0 }"
    >
      <v-container class="landing-wrap">
        <v-row align="center" :class="{ 'flex-md-row-reverse': index % 2 === 1 }">
          <v-col cols="12" md="5" :class="index % 2 === 1 ? 'pl-md-10' : 'pr-md-10'">
            <p class="landing-kicker">{{ feature.kicker }}</p>
            <h2 class="landing-h2">{{ feature.title }}</h2>
            <p class="landing-text" v-html="feature.text"></p>
            <ul class="landing-checks">
              <li v-for="bullet in feature.bullets" :key="bullet">
                <v-icon small color="success">mdi-check-circle</v-icon>
                <span>{{ bullet }}</span>
              </li>
            </ul>
          </v-col>
          <v-col cols="12" md="7">
            <figure class="landing-figure">
              <app-preview :variant="feature.mockup" />
              <figcaption>{{ l.mockupCaption }}</figcaption>
            </figure>
          </v-col>
        </v-row>
      </v-container>
    </section>

    <section class="landing-audiences">
      <v-container class="landing-wrap">
        <v-row>
          <v-col v-for="audience in l.audiences" :key="audience.id" cols="12" md="6">
            <div class="landing-card">
              <v-icon large color="success darken-1">{{ audience.icon }}</v-icon>
              <h2 class="landing-h3">{{ audience.title }}</h2>
              <ul class="landing-checks">
                <li v-for="item in audience.items" :key="item">
                  <v-icon small color="success">mdi-check-circle</v-icon>
                  <span>{{ item }}</span>
                </li>
              </ul>
            </div>
          </v-col>
        </v-row>
      </v-container>
    </section>

    <section class="landing-trust">
      <v-container class="landing-wrap">
        <h2 class="landing-h2 text-center">{{ l.trust.title }}</h2>
        <v-row>
          <v-col v-for="item in l.trust.items" :key="item.title" cols="12" sm="6" md="3">
            <div class="landing-trust-item">
              <v-icon large color="light-green lighten-2">{{ item.icon }}</v-icon>
              <h3>{{ item.title }}</h3>
              <p>{{ item.text }}</p>
            </div>
          </v-col>
        </v-row>
        <p class="text-center mt-6 mb-0">
          <router-link to="/datenschutz" class="landing-trust-link">Zur Datenschutzerklärung</router-link>
        </p>
      </v-container>
    </section>

    <section class="landing-faq">
      <v-container class="landing-narrow">
        <h2 class="landing-h2 text-center">{{ l.faqTitle }}</h2>
        <v-expansion-panels flat accordion>
          <v-expansion-panel v-for="item in l.faq" :key="item.q" class="landing-faq-item">
            <v-expansion-panel-header class="landing-faq-q">{{ item.q }}</v-expansion-panel-header>
            <v-expansion-panel-content eager>
              <p class="landing-text mb-0" v-html="item.a"></p>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
      </v-container>
    </section>

    <section class="landing-cta">
      <v-container class="landing-narrow text-center">
        <h2 class="landing-h2">{{ l.cta.title }}</h2>
        <p class="landing-text">{{ l.cta.text }}</p>
        <v-btn x-large depressed color="success" class="mt-4" :to="l.cta.primary.to">
          {{ l.cta.primary.text }}
          <v-icon right>mdi-arrow-right</v-icon>
        </v-btn>
      </v-container>
    </section>
  </div>
</template>

<script>
import publicPages from '@/content/publicPages';
import AppPreview from './landing/AppPreview.vue';

export default {
  name: 'welcome',

  components: { AppPreview },

  data: () => ({
    l: publicPages.landing
  })
};
</script>

<style scoped>
.landing {
  --ink: #1d2a1f;
  --muted: #526255;
  --green: #2e7d32;
  --tint: #f3f8f1;
  color: var(--ink);
  background: #fff;
}

.landing-wrap {
  max-width: 1180px;
}

.landing-narrow {
  max-width: 780px;
}

.landing-hero {
  padding: 40px 0 32px;
  background: linear-gradient(180deg, #eaf5e5 0%, #fff 100%);
}

.landing-kicker {
  margin-bottom: 0.75rem;
  color: var(--green);
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.landing-h1 {
  margin-bottom: 1.25rem;
  font-size: clamp(2rem, 4.2vw, 3.1rem);
  font-weight: 800;
  line-height: 1.12;
  letter-spacing: -0.02em;
}

.landing-lead {
  max-width: 36em;
  color: var(--muted);
  font-size: 1.15rem;
  line-height: 1.65;
}

.landing-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 28px;
}

.landing-note {
  margin: 18px 0 0;
  color: var(--muted);
  font-size: 0.9rem;
}

.landing-hero-visual {
  position: relative;
}

.landing-hero-phone {
  position: absolute;
  right: 0;
  bottom: 0;
  width: 220px;
  transform: rotate(2deg);
}

.landing-h2 {
  margin-bottom: 1rem;
  font-size: clamp(1.5rem, 2.6vw, 2.1rem);
  font-weight: 800;
  line-height: 1.2;
  letter-spacing: -0.01em;
}

.landing-h3 {
  margin: 12px 0;
  font-size: 1.3rem;
  font-weight: 700;
}

.landing-text {
  color: var(--muted);
  font-size: 1.05rem;
  line-height: 1.7;
}

.landing-text >>> a {
  color: var(--green);
}

.landing-intro {
  padding: 56px 0 32px;
  text-align: center;
}

.landing-feature {
  padding: 56px 0;
}

.landing-feature--tinted {
  background: var(--tint);
}

.landing-checks {
  margin: 16px 0 0;
  padding: 0 !important;
  list-style: none;
}

.landing-checks li {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 5px 0;
  line-height: 1.5;
}

.landing-checks .v-icon {
  margin-top: 3px;
}

.landing-figure {
  margin: 0;
}

.landing-figure figcaption {
  margin-top: 10px;
  color: #8a968c;
  font-size: 0.78rem;
  text-align: center;
}

.landing-audiences {
  padding: 64px 0;
}

.landing-card {
  height: 100%;
  padding: 28px;
  border: 1px solid #e1e9dd;
  border-radius: 14px;
  background: #fff;
}

.landing-trust {
  padding: 64px 0;
  background: #1f3d23;
  color: #fff;
}

.landing-trust .landing-h2 {
  margin-bottom: 32px;
}

.landing-trust-item h3 {
  margin: 10px 0 6px;
  font-size: 1.05rem;
  font-weight: 700;
}

.landing-trust-item p {
  margin: 0;
  color: rgba(255, 255, 255, 0.8);
  line-height: 1.6;
}

.landing-trust-link {
  color: #c5e8b7 !important;
}

.landing-faq {
  padding: 64px 0;
}

.landing-faq-item {
  border-bottom: 1px solid #e1e9dd;
}

.landing-faq-q {
  font-size: 1.05rem;
  font-weight: 600;
}

.landing-cta {
  padding: 72px 0 88px;
  background: var(--tint);
}

@media (min-width: 600px) {
  .landing-hero-visual {
    padding: 0 90px 60px 0;
  }
}

@media (min-width: 960px) {
  .landing-hero {
    padding: 88px 0 72px;
  }

  .landing-feature {
    padding: 80px 0;
  }
}
</style>
